import { isEmpty, noop } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEnvelopeDot,
  faEnvelopeOpen,
  faPen,
  faShelves,
  faUser,
} from '@fortawesome/pro-regular-svg-icons'
import {
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  Truncate,
  VerticalDivider,
} from '@labrav/react-components'
import { OfferEndBuyers } from '../../../types/flyout'
import { useStyles } from './ViewOfferEndBuyers.styles'
import { DetailRow } from '../DetailRow/DetailRow'
import { RecipentStakeholderRow } from '../RecipientStakeholderRow/RecipentStakeholderRow'
import { SectionWrapper } from '../SectionWrapper/SectionWrapper'
import { Dispatch, SetStateAction } from 'react'
import clsx from 'clsx'
import { EditOfferEndBuyers } from '../EditOfferEndBuyers/EditOfferEndBuyers'
import { ViewValueWithTruncate } from '@labrav/common'
import emptyBuyer from '../../images/emptyBuyer.png'
import { useTheme } from '@mui/material/styles'

export const ViewOfferEndBuyers = ({
  offerEndBuyers,
  isOfferEndBuyersEditActionDisabled,
  addOfferEndBuyersResaleToolTipMessage,
  hideEditActionOfferEndBuyers,
  handleSubmitOfferEndBuyers,
  openEditOfferEndBuyers,
  setOpenEditOfferEndBuyers,
  isUpdateLoading,
}: {
  offerEndBuyers: OfferEndBuyers[]
  isOfferEndBuyersEditActionDisabled?: boolean
  addOfferEndBuyersResaleToolTipMessage?: string
  hideEditActionOfferEndBuyers?: boolean
  handleSubmitOfferEndBuyers?: (offerEndBuyers: OfferEndBuyers[]) => void
  setOpenEditOfferEndBuyers: Dispatch<SetStateAction<boolean>>
  openEditOfferEndBuyers: boolean
  isUpdateLoading?: boolean
}) => {
  const { classes } = useStyles()
  const theme = useTheme()

  const editAction = (
    <div
      className={clsx(
        classes.editActionLabel,
        isOfferEndBuyersEditActionDisabled ? classes.disabledEdit : ''
      )}
      data-testid={'edit-action'}
    >
      <FontAwesomeIcon
        icon={faPen}
        style={{
          width: '14px',
          height: '14px',
          fontSize: '14px',
          padding: '0px 9px 0px 0px',
          color: theme.palette.primary.main,
          opacity: isOfferEndBuyersEditActionDisabled ? '39%' : '100%',
        }}
      />
      <div>Edit</div>{' '}
    </div>
  )

  const isEmptyBuyerDetails = isEmpty(offerEndBuyers) || !offerEndBuyers?.length

  const offerEndBuyersValue = offerEndBuyers?.length
    ? offerEndBuyers
    : ([
        {
          firstName: '',
          lastName: '',
          email: '',
          title: '',
        },
      ] as OfferEndBuyers[])
  return (
    <>
      {!isEmptyBuyerDetails ? (
        <SectionWrapper
          heading="Buyer details"
          icon={faUser}
          className={classes.rightSection}
          handleAction={() => setOpenEditOfferEndBuyers(true)}
          actionDisabled={isOfferEndBuyersEditActionDisabled}
          actionDisabledTooltip={
            isOfferEndBuyersEditActionDisabled
              ? addOfferEndBuyersResaleToolTipMessage
              : ''
          }
          varient="small"
          {...(!hideEditActionOfferEndBuyers
            ? { actionLabel: editAction }
            : {})}
          children={
            <div
              className={classes.section}
              data-testid={'offer-end-buyers-view'}
            >
              {(offerEndBuyersValue || []).map((item, index) => {
                const isTitleExist = !!item?.title
                return (
                  <DetailRow
                    key={`${item.email}-${index}`}
                    borderBottom={index !== offerEndBuyersValue?.length - 1}
                    className={clsx(
                      classes.rowItem,
                      index === (offerEndBuyersValue || []).length - 1
                        ? classes.lastRowItem
                        : ''
                    )}
                    data-testid="detailed-row"
                    children={
                      <RecipentStakeholderRow
                        title={
                          <div className={classes.recipientTitle}>
                            <div className={classes.recipientTitleWrapper}>
                              <div
                                data-testid={'buyer-name'}
                                className={clsx(
                                  classes.recipientName,
                                  !isTitleExist &&
                                    classes.recipientNameWithoutTitle
                                )}
                              >
                                <ViewValueWithTruncate
                                  width="100%"
                                  value={`${item?.firstName || ''} ${item?.lastName || ''}`.trim()}
                                />
                              </div>
                              {item?.title && (
                                <>
                                  <div>
                                    <VerticalDivider height="24px" />
                                  </div>
                                  <div
                                    data-testid={'buyer-title'}
                                    className={classes.recipientTitleName}
                                  >
                                    <ViewValueWithTruncate
                                      width="100%"
                                      value={item?.title}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                            {(!isEmpty(item?.lastViewedAt) ||
                              !isEmpty(item?.sentAt)) && (
                              <LabraToolTipV2
                                description={
                                  !isEmpty(item?.lastViewedAt)
                                    ? 'Viewed'
                                    : 'Not viewed'
                                }
                                tooltipPlacmentGap="8px"
                                tooltipPlacement={'bottom'}
                              >
                                <FontAwesomeIcon
                                  icon={
                                    !isEmpty(item?.lastViewedAt)
                                      ? faEnvelopeOpen
                                      : faEnvelopeDot
                                  }
                                  style={{
                                    width: '18px',
                                    height: '20px',
                                    fontSize: '16px',
                                    color: theme.palette.labraText.description,
                                  }}
                                />
                              </LabraToolTipV2>
                            )}
                          </div>
                        }
                        value={
                          <Truncate
                            width={'90%'}
                            clipboardTooltip
                            showIcon={false}
                          >
                            {item?.email}
                          </Truncate>
                        }
                        keyName={'recipient'}
                      />
                    }
                  />
                )
              })}
            </div>
          }
        />
      ) : (
        <div className={classes.emptyStateContent}>
          <div className={classes.emptyState}>
            <img src={emptyBuyer} />
          </div>
          <div className={classes.emptyRightSection}>
            <div className={classes.emptyStateText}>
              Add buyer details to trigger reminder emails before offer expiry.
            </div>
            <ButtonV2
              className={classes.addBtn}
              onClick={() => setOpenEditOfferEndBuyers(true)}
            >
              Add
            </ButtonV2>
          </div>
        </div>
      )}
      <ModalConfirmV2
        title={'Edit buyer details'}
        content={
          <EditOfferEndBuyers
            offerEndBuyers={offerEndBuyersValue}
            handleCloseOfferEndBuyers={() => setOpenEditOfferEndBuyers(false)}
            handleSubmit={handleSubmitOfferEndBuyers}
            isUpdateLoading={isUpdateLoading}
          />
        }
        hideCancelButton={true}
        open={openEditOfferEndBuyers}
        hideAcceptButton={true}
        onAccept={noop}
        onDecline={() => setOpenEditOfferEndBuyers(false)}
        variant={ModalSizeVariants.large}
        icon={<FontAwesomeIcon style={{ height: '14px' }} icon={faShelves} />}
      />
    </>
  )
}
